import React, { useState } from "react";

interface EditSubzoneModalProps {
  initialData: {
    description?: string;
    latitude?: number | string;
    longitude?: number | string;
    name: string;
    zone_id: number | string;
  };
  onClose: () => void;
  onSubmit: (data: {
    description?: string;
    latitude?: number | string;
    longitude?: number | string;
    name: string;
    zone_id: number | string;
  }) => Promise<void>;
  open: boolean;
  zones: Array<{ id: number | string; name: string }>;
}

const EditSubzoneModal: React.FC<EditSubzoneModalProps> = ({
  initialData,
  onClose,
  onSubmit,
  open,
  zones,
}) => {
  const [name, setName] = useState(initialData.name);
  const [description, setDescription] = useState(initialData.description || "");
  const [zoneId, setZoneId] = useState(initialData.zone_id);
  const [latitude, setLatitude] = useState(
    initialData.latitude ? String(initialData.latitude) : "",
  );
  const [longitude, setLongitude] = useState(
    initialData.longitude ? String(initialData.longitude) : "",
  );

  const isLatitudeValid = (lat: string) => {
    const n = Number(lat);
    return lat !== "" && !isNaN(n) && n >= -90 && n <= 90;
  };
  const isLongitudeValid = (lng: string) => {
    const n = Number(lng);
    return lng !== "" && !isNaN(n) && n >= -180 && n <= 180;
  };
  const isFormValid =
    name.trim() !== "" &&
    zoneId &&
    isLatitudeValid(latitude) &&
    isLongitudeValid(longitude);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Edit Subzone
        </h3>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await onSubmit({
              description,
              latitude: latitude !== "" ? Number(latitude) : undefined,
              longitude: longitude !== "" ? Number(longitude) : undefined,
              name,
              zone_id: zoneId,
            });
          }}
        >
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Latitude
            </label>
            <input
              className={`mt-1 w-full rounded border px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${latitude && !isLatitudeValid(latitude) ? "border-red-500" : ""}`}
              max="90"
              min="-90"
              onChange={(e) => setLatitude(e.target.value)}
              placeholder="Latitude (-90 to 90)"
              required
              step="any"
              type="number"
              value={latitude}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Longitude
            </label>
            <input
              className={`mt-1 w-full rounded border px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${longitude && !isLongitudeValid(longitude) ? "border-red-500" : ""}`}
              max="180"
              min="-180"
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="Longitude (-180 to 180)"
              required
              step="any"
              type="number"
              value={longitude}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Subzone Name
            </label>
            <input
              className="mt-1 w-full rounded border px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              onChange={(e) => setName(e.target.value)}
              required
              type="text"
              value={name}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <input
              className="mt-1 w-full rounded border px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              onChange={(e) => setDescription(e.target.value)}
              type="text"
              value={description}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Zone
            </label>
            <select
              className="mt-1 w-full rounded border px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              onChange={(e) => setZoneId(e.target.value)}
              required
              value={zoneId}
            >
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button
              className="rounded bg-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
              disabled={!isFormValid}
              type="submit"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubzoneModal;
